import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { forgotPassword } from "../../api/auth";

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>;
}

function Label({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} style={{
      fontSize: "var(--text-sm)",
      fontWeight: 600,
      color: "var(--text-secondary)",
      fontFamily: "var(--font-sans)",
    }}>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        height: "var(--control-md)",
        padding: "0 12px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        color: "var(--text-primary)",
        fontSize: "var(--text-base)",
        fontFamily: "var(--font-sans)",
        outline: "none",
        boxSizing: "border-box",
        ...props.style,
      }}
    />
  );
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");

  const mutation = useMutation({
    mutationFn: forgotPassword,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ email });
  };

  return (
    <div style={{
      background: "var(--surface-card)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--radius-xl)",
      padding: "var(--space-8)",
      boxShadow: "var(--shadow-md), var(--ring-top)",
    }}>
      <div style={{ marginBottom: "var(--space-7)" }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)" }}>
          Account recovery
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          Reset your password
        </h1>
      </div>

      {mutation.isSuccess ? (
        <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5 }}>
          If an account exists for that email, a reset link is on its way. Check
          your inbox.
        </p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <FieldGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </FieldGroup>

          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              width: "100%",
              height: "var(--control-lg)",
              background: mutation.isPending ? "var(--accent-press)" : "var(--accent)",
              color: "var(--text-on-gold)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-base)",
              fontWeight: 600,
              cursor: mutation.isPending ? "not-allowed" : "pointer",
              transition: "background 0.15s ease",
            }}
          >
            {mutation.isPending ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}

      <p style={{
        marginTop: "var(--space-6)",
        textAlign: "center",
        fontSize: "var(--text-sm)",
        color: "var(--text-muted)",
        fontFamily: "var(--font-sans)",
      }}>
        <Link to="/login" style={{ color: "var(--text-accent)", fontWeight: 600 }}>
          ← Back to sign in
        </Link>
      </p>
    </div>
  );
}
